// @ts-strict-ignore
import { Collapse, Icon } from '@blueprintjs/core'
import classNames from 'classnames'
import React, { ReactNode, useState } from 'react'
import { TextInput } from '../inputs/Input'
import { Button } from '../ui/button'

import { Rotate } from '../ui/Rotate'

interface SettingsCardProps {
    title: string | ReactNode
    children: ReactNode
    className?: string
    description?: string | JSX.Element
    onSave?: any
    loading: boolean
    open?: boolean
}

export const SettingsCard = ({
    title,
    description,
    children,
    className,
    onSave,
    loading,
    open = false,
}: SettingsCardProps) => {
    const [isOpen, setIsOpen] = useState<boolean>(open)
    const toggleOpen = () => {
        setIsOpen((p) => !p)
    }

    const style = {
        padding: '25px 20px',
        borderRadius: 6,
        border: '1px solid #DBE2E8',
    }

    return (
        <div style={style} className={classNames('bg-white w-full h-full mb-2', className)}>
            <div className="flex justify-between">
                <div
                    className="text-mjDark-1400 text-base font-semibold cursor-pointer flex items-center"
                    onClick={toggleOpen}
                >
                    <div>{title}</div>
                    <div className="pl-1.5">
                        <Rotate rotateDeg={isOpen ? 90 : 0}>
                            <Icon icon="chevron-right" />
                        </Rotate>
                    </div>
                </div>
                {onSave && (
                    <Button onClick={onSave} loading={loading} intent="primary" text="Save" />
                )}
            </div>
            <Collapse isOpen={isOpen}>
                {description && (
                    <p
                        className="mt-3 mb-3 w-full lg:w-1/4"
                        style={{ color: '#899CB3', fontSize: 13, overflowWrap: 'anywhere' }}
                    >
                        {description}
                    </p>
                )}
                <div className="w-full h-full">{children}</div>
            </Collapse>
        </div>
    )
}

interface SettingItemProps {
    title: string
    description: string | JSX.Element
    bottomDivider?: boolean
    rightComponent?: ReactNode
}

export const SettingItem = ({
    description,
    title,
    bottomDivider = true,
    rightComponent,
}: SettingItemProps) => {
    const style = {
        paddingLeft: 5,
        paddingTop: 25,
        paddingBottom: 10,
    }

    return (
        <div style={style} className="w-full">
            <div className="flex flex-col gap-4 lg:flex-row">
                <div className="w-full min-w-0 pr-0 lg:w-1/4 lg:flex-none lg:pr-5">
                    <h4 className="font-medium text-sm text-mjDark-800 pb-2 break-words">
                        {title}
                    </h4>
                    <p
                        className="font-normal break-words"
                        style={{ color: '#899CB3', fontSize: 13, overflowWrap: 'anywhere' }}
                    >
                        {description}
                    </p>
                </div>
                <div className="flex w-full items-start lg:flex-1">{rightComponent}</div>
            </div>
            {bottomDivider && <hr style={{ marginTop: 20 }} />}
        </div>
    )
}

// Display the text value of the setting.
// This is non-interactive and is meant to display fixed values.
export const SettingsText = ({ text }) => {
    return <p className="flex items-end text-sm text-mjDark-1400">{text}</p>
}

export const SettingsTextInput = ({ onChange, loading, value }) => {
    return (
        <div className="flex items-end pb-2">
            <TextInput loading={loading} className={'w-64'} onChange={onChange} value={value} />
        </div>
    )
}
